import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Form, Modal, Space, Spin } from 'antd';
import { copiarVisibilidadEmpleado, leerVisibilidadEmpleado } from '../../api/empleadosApi';
import type { CopiaVisibilidadResultado, EmpleadoVisibilidad } from '../../api/empleadosApi';
import { FctAviso } from '../../fct/react';
import { errorEmpleado } from './errores';
import { VisibilidadSelectorEmpleado } from './VisibilidadSelectorEmpleado';

export const AYUDA_COPIA = 'Copia a este empleado lo que ve otro: sus asesores sustituyen a los actuales y las subregiones abiertas se abren desde hoy.';
export const AVISO_COPIA = 'Esta copia cambia lo que esta persona ve en Gescom desde ahora.';

/** Lo que la copia va a cambiar, calculado con la visibilidad de los dos empleados (el servidor aplica la misma regla). */
export interface RecuentoCopia {
  /** Asesores ajenos que tendrá tras la copia (los del origen, sin su fila propia ni el propio destino). */
  asesoresResultantes: number;
  /** Asesores ajenos que tiene ahora y se sustituyen. */
  asesoresActuales: number;
  /** Subregiones abiertas del origen que el destino aún no tiene abiertas. La subregión 0 no se copia. */
  subregionesNuevas: number;
}

export const recuentoCopia = (origen: EmpleadoVisibilidad, destino: EmpleadoVisibilidad, idDestino: number): RecuentoCopia => {
  const abiertasDestino = new Set(destino.subregiones.filter((s) => !s.hasta).map((s) => s.idSubregion));
  const nuevas = new Set(origen.subregiones.filter((s) => !s.hasta && s.idSubregion !== 0 && !abiertasDestino.has(s.idSubregion)).map((s) => s.idSubregion));
  return {
    asesoresResultantes: origen.asesores.filter((a) => !a.propia && a.idAsesor !== idDestino).length,
    asesoresActuales: destino.asesores.filter((a) => !a.propia).length,
    subregionesNuevas: nuevas.size,
  };
};

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

interface ContenidoProps {
  idDestino: number;
  onTerminado: (r: CopiaVisibilidadResultado) => void;
  onCancelar: () => void;
}

/** «Copiar de otro empleado»: origen, qué se copia, confirmación con recuentos y resultado. */
export const CopiarVisibilidad: React.FC<ContenidoProps> = ({ idDestino, onTerminado, onCancelar }) => {
  const [destino, setDestino] = useState<EmpleadoVisibilidad | null>(null);
  const [origen, setOrigen] = useState<EmpleadoVisibilidad | null>(null);
  const [idOrigen, setIdOrigen] = useState<number | undefined>();
  const [asesores, setAsesores] = useState(true);
  const [subregiones, setSubregiones] = useState(true);
  const [paso, setPaso] = useState<'elegir' | 'confirmar'>('elegir');
  const [cargando, setCargando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<CopiaVisibilidadResultado | null>(null);

  useEffect(() => {
    let vigente = true;
    leerVisibilidadEmpleado(idDestino).then((v) => { if (vigente) setDestino(v); }).catch((e) => { if (vigente) setError(errorEmpleado(e).mensaje); });
    return () => { vigente = false; };
  }, [idDestino]);

  const elegirOrigen = (id: number | undefined) => {
    setIdOrigen(id);
    setOrigen(null);
    setError(null);
  };

  const continuar = async () => {
    if (idOrigen === undefined) return;
    setCargando(true);
    setError(null);
    try {
      setOrigen(await leerVisibilidadEmpleado(idOrigen));
      setPaso('confirmar');
    } catch (e) {
      setError(errorEmpleado(e).mensaje);
    } finally {
      setCargando(false);
    }
  };

  const aplicar = async () => {
    if (idOrigen === undefined) return;
    setAplicando(true);
    setError(null);
    try {
      setResultado(await copiarVisibilidadEmpleado(idDestino, { idOrigen, asesores, subregiones }));
    } catch (e) {
      setError(errorEmpleado(e).mensaje);
    } finally {
      setAplicando(false);
    }
  };

  if (resultado) {
    return (
      <div>
        <FctAviso tipo="exito" titulo="Copia aplicada.">
          {[asesores && `Asesores que ve ahora: ${resultado.asesores}.`, subregiones && `Subregiones abiertas: ${resultado.subregionesAbiertas}.`].filter(Boolean).join(' ')}
        </FctAviso>
        <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Button type="primary" onClick={() => onTerminado(resultado)}>Hecho</Button>
        </Space>
      </div>
    );
  }

  const recuento = paso === 'confirmar' && origen && destino ? recuentoCopia(origen, destino, idDestino) : null;

  return (
    <div>
      <p style={{ margin: '0 0 8px' }}>{AYUDA_COPIA}</p>
      {paso === 'elegir' && (
        <Form layout="vertical" size="small" onFinish={continuar}>
          <Form.Item label="Copiar de" htmlFor="visibilidad-origen">
            <VisibilidadSelectorEmpleado id="visibilidad-origen" value={idOrigen} excluir={[idDestino]} onChange={elegirOrigen} />
          </Form.Item>
          <Form.Item label="Qué se copia" style={{ marginBottom: 8 }}>
            <Checkbox checked={asesores} onChange={(e) => setAsesores(e.target.checked)}>Asesores que ve</Checkbox>
            <Checkbox checked={subregiones} onChange={(e) => setSubregiones(e.target.checked)}>Subregiones abiertas</Checkbox>
          </Form.Item>
          {!asesores && !subregiones && <p role="status" style={{ margin: '0 0 8px' }}>Marca al menos una de las dos cosas.</p>}
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onCancelar}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={cargando} disabled={idOrigen === undefined || (!asesores && !subregiones)}>Revisar cambios</Button>
          </Space>
        </Form>
      )}
      {paso === 'confirmar' && !recuento && !error && <Spin />}
      {recuento && (
        <div>
          <FctAviso tipo="aviso" titulo="Vas a cambiar lo que ve esta persona.">{AVISO_COPIA}</FctAviso>
          <ul style={{ margin: '8px 0', paddingLeft: 18 }}>
            {asesores && (
              <li>
                Asesores: {plural(recuento.asesoresActuales, 'asesor actual se sustituye', 'asesores actuales se sustituyen')} por {plural(recuento.asesoresResultantes, 'asesor', 'asesores')}.
              </li>
            )}
            {subregiones && <li>Subregiones: se abrirán {plural(recuento.subregionesNuevas, 'subregión nueva', 'subregiones nuevas')} desde hoy.</li>}
          </ul>
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setPaso('elegir'); setError(null); }}>Volver</Button>
            <Button type="primary" loading={aplicando} onClick={aplicar}>Aplicar copia</Button>
          </Space>
        </div>
      )}
    </div>
  );
};

interface ModalProps extends Omit<ContenidoProps, 'onCancelar'> {
  abierto: boolean;
  onCerrar: () => void;
}

/** La copia en su propio modal, desde la pestaña Visibilidad. */
export const CopiarVisibilidadModal: React.FC<ModalProps> = ({ abierto, idDestino, onTerminado, onCerrar }) => (
  <Modal open={abierto} onCancel={onCerrar} title="Copiar de otro empleado" footer={null} destroyOnClose maskClosable={false}>
    {abierto && <CopiarVisibilidad idDestino={idDestino} onTerminado={onTerminado} onCancelar={onCerrar} />}
  </Modal>
);

/** Paso tras un alta: el empleado ya existe y se ofrece abrir la copia antes de ir a su ficha. */
export const VisibilidadPostAlta: React.FC<{ idEmpleado: number; onAbrirFicha: () => void }> = ({ idEmpleado, onAbrirFicha }) => {
  const [copiando, setCopiando] = useState(false);
  return (
    <div>
      <FctAviso tipo="exito" titulo="Empleado creado.">¿Quieres darle la misma visibilidad que otro empleado?</FctAviso>
      {copiando ? (
        <div style={{ marginTop: 12 }}>
          <CopiarVisibilidad idDestino={idEmpleado} onTerminado={onAbrirFicha} onCancelar={() => setCopiando(false)} />
        </div>
      ) : (
        <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Button onClick={onAbrirFicha}>Ahora no, abrir ficha</Button>
          <Button type="primary" onClick={() => setCopiando(true)}>Copiar de otro empleado</Button>
        </Space>
      )}
    </div>
  );
};
